import type { Connection, VersionedTransaction, VersionedTransactionResponse } from "@solana/web3.js";

import { ReactComponent as PendingIcon } from "../../assets/notifications/pendingIcon.svg";
import { confirmOptions } from "../../constants/transaction";
import type { ConnectedWallet } from "../../hooks";
import { notify } from "../notifications";
import { promiseDelay } from "../utils";
import { sendSuccessContent } from "./notifications";
import { sendPostSendTxnErrorContent, StepTxnTimeoutError } from "./notifications/handleTransactionErrors";
import {
  DEFAULT_APPROVE_TXN_MESSAGE,
  DEFAULT_IN_PROGRESS_NOTIFICATION_CONTENT,
  DEFAULT_NOTIFICATION_STATES,
  DEFAULT_SUCCESS_NOTIFICATION_CONTENT,
} from "./sendTransaction";

export const sendVersion0Transaction = async (
  connection: Connection,
  wallet: ConnectedWallet,
  transaction: VersionedTransaction,
  awaitConfirmation = true,
  options = confirmOptions,
  renderNotifications = false,
  notificationContentOptions = DEFAULT_NOTIFICATION_STATES,
  onTransactionInProgress?: () => void,
) => {
  const notificationContent = {
    approveMessage: notificationContentOptions.approveMessage ?? DEFAULT_APPROVE_TXN_MESSAGE,
    inProgress: notificationContentOptions.inProgress ?? DEFAULT_IN_PROGRESS_NOTIFICATION_CONTENT,
    success: notificationContentOptions.success ?? DEFAULT_SUCCESS_NOTIFICATION_CONTENT,
  };

  if (typeof notificationContent.inProgress === "object" && !notificationContent.inProgress.icon) {
    notificationContent.inProgress.icon = <PendingIcon />;
  }
  notify({
    type: "open",
    message: notificationContent.approveMessage,
  });

  if (onTransactionInProgress) {
    onTransactionInProgress();
  }

  const signedTx = await wallet.signTransaction(transaction);
  const rawTx = signedTx.serialize();
  try {
    const txid = await connection.sendRawTransaction(Buffer.from(rawTx), {
      ...options,
      maxRetries: 2,
    });
    sendSuccessContent(renderNotifications, txid, notificationContent.inProgress);
    if (awaitConfirmation) {
      const delayTime = 500;
      let attempts = 10;
      let response: VersionedTransactionResponse | null = null;
      while (!response && attempts) {
        response = await connection.getTransaction(txid, {
          commitment: "confirmed",
          maxSupportedTransactionVersion: 2,
        });
        await promiseDelay(delayTime);
        attempts -= 1;
      }
      if (response) {
        if (!response.meta?.err) {
          sendSuccessContent(renderNotifications, txid, notificationContent.success);
        } else {
          sendPostSendTxnErrorContent(txid, response);
        }
      } else {
        throw new StepTxnTimeoutError(
          `Transaction was not confirmed in ${((attempts * delayTime) / 1000).toFixed(0)} seconds.`,
          txid,
        );
      }
    }
  } catch (e) {
    console.error("Transaction Error:", e);
    throw e;
  }
};
